import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from './ui/card';
import { Edit } from 'lucide-react';
import { Button } from './ui/button';
import { LibraryIcon, isLibraryIcon } from '../lib/iconLibrary';
import { autoIconColor } from '../lib/colorContrast';

export default function DraggableTile({ tile, onEdit, onDelete, style: tileStyle, shape, tileFontColor, tileColor }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tile.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleTileClick = (e) => {
    // Don't trigger if clicking the delete button
    if (e.target.closest('button')) return;
    onEdit(tile);
  };

  const lightenColor = (color, percent) => {
    // Convert hex to RGB
    let r, g, b;
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else if (color.startsWith('rgb')) {
      const matches = color.match(/\d+/g);
      [r, g, b] = matches.map(Number);
    } else {
      return color;
    }
    
    // Lighten by moving towards white
    r = Math.min(255, Math.round(r + (255 - r) * percent));
    g = Math.min(255, Math.round(g + (255 - g) * percent));
    b = Math.min(255, Math.round(b + (255 - b) * percent));
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  const getTileStyle = () => {
    const baseColor = tile.color || tileColor || '#2563EB';
    const perTileOverride = tile.text_color && tile.text_color !== '#FFFFFF' ? tile.text_color : null;

    switch (tileStyle) {
      case 'solid':
        return {
          backgroundColor: baseColor,
          color: tile.text_color || tileFontColor || '#FFFFFF'
        };
      case 'gradient':
        const lightColor = lightenColor(baseColor, 0.5);
        return {
          background: `linear-gradient(135deg, ${baseColor} 0%, ${lightColor} 100%)`,
          color: tile.text_color || tileFontColor || '#FFFFFF'
        };
      case 'outlined':
        // "Outlined" fills white by default, but an explicit per-tile background
        // colour must still paint the background rather than only the border.
        return tile.color
          ? {
              backgroundColor: baseColor,
              border: `2px solid ${tile.border_color || baseColor}`,
              color: autoIconColor(baseColor, tile.text_color)
            }
          : {
              backgroundColor: '#FFFFFF',
              border: `2px solid ${tile.border_color || baseColor}`,
              color: perTileOverride || tileFontColor || baseColor
            };
      default: // soft
        return {
          backgroundColor: `${baseColor}15`,
          color: perTileOverride || tileFontColor || baseColor
        };
    }
  };

  const getShapeClass = () => {
    switch (shape) {
      case 'circle':
        return 'rounded-full aspect-square';
      case 'rounded':
        return 'rounded-2xl';
      case 'square':
        return 'rounded-none';
      default:
        return 'rounded-xl';
    }
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...attributes}
      className="relative"
      data-testid={`draggable-tile-${tile.id}`}
    >
      {/* For circle tiles: delete badge sits outside the circle as a corner overlay */}
      {shape === 'circle' && (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(tile.id);
          }}
          className="absolute -top-1.5 -right-1.5 z-20 h-6 w-6 p-0 rounded-full bg-red-500 hover:bg-red-600 shadow-md"
          data-testid={`delete-tile-${tile.id}`}
        >
          <span className="text-white text-sm leading-none">×</span>
        </Button>
      )}

      <Card
        className={`relative ${shape !== 'circle' ? 'overflow-hidden' : ''} h-full hover:shadow-lg hover:scale-105 transition-all cursor-pointer ${getShapeClass()}`}
        style={getTileStyle()}
        onClick={handleTileClick}
      >
        <CardContent className="p-6 h-full flex flex-col items-center justify-center text-center">
          <div 
            className="absolute top-1 left-1 cursor-move touch-none p-2 opacity-50 hover:opacity-100 transition-opacity"
            {...listeners}
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-xs">⋮⋮</span>
          </div>
          
          {/* For non-circle tiles: delete button inside card (works because overflow-hidden is present) */}
          {shape !== 'circle' && (
            <div className="absolute top-1 right-1 flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(tile.id);
                }}
                className="h-7 w-7 p-0 opacity-50 hover:opacity-100 bg-red-500/10 hover:bg-red-500/20"
                data-testid={`delete-tile-${tile.id}`}
              >
                <span className="text-red-600">×</span>
              </Button>
            </div>
          )}
          
          {tile.icon ? (
            <div className="text-4xl mb-3 flex items-center justify-center" style={{ width: '3rem', height: '3rem' }}>
              {tile.icon.startsWith('data:') || tile.icon.startsWith('http') ? (
                <img 
                  src={tile.icon} 
                  alt="" 
                  className="w-full h-full object-contain rounded-lg"
                />
              ) : isLibraryIcon(tile.icon) ? (
                <LibraryIcon
                  iconString={tile.icon}
                  className="w-full h-full"
                  color={autoIconColor(tile.color, tile.icon_color)}
                />
              ) : (
                <span>{tile.icon}</span>
              )}
            </div>
          ) : (
            <div className="mb-3 flex items-center justify-center rounded-lg bg-amber-100 border border-amber-300" style={{ width: '3rem', height: '3rem' }}>
              <span className="text-amber-700 text-xs font-bold" title="No icon set">!</span>
            </div>
          )}
          
          <h3 className="font-semibold text-sm mb-1" style={{ color: getTileStyle().color }}>
            {tile.title || <span className="text-amber-600 italic">(no title — click to fix)</span>}
          </h3>
          
          <p className="text-xs text-slate-500">Click to edit</p>
        </CardContent>
      </Card>
    </div>
  );
}